import { ApolloClient, gql } from 'apollo-boost';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';

const newNpoAnswerQuery = gql`
  mutation newNpoAnswer(
    $questionId: Int!
    $reportId: Int!
    $text: String
    $date1: Date
    $date2: Date
    $answerId: Int
    $questionText: String
  ) {
    newNpoAnswer(
      questionId: $questionId
      reportId: $reportId
      text: $text
      answerId: $answerId
      date1: $date1
      date2: $date2
      questionText: $questionText
    ) {
      id
      success
      nextQuestion
    }
  }
`;

const updateNpoAnswerQuery = gql`
  mutation updateNpoAnswer(
    $questionId: Int!
    $reportId: Int!
    $text: String
    $answerId: Int
    $id: Int!
    $date1: Date
    $date2: Date
    $questionText: String
    $ignoreClearData: Boolean
  ) {
    updateNpoAnswer(
      questionId: $questionId
      reportId: $reportId
      text: $text
      id: $id
      answerId: $answerId
      date1: $date1
      date2: $date2
      questionText: $questionText
      ignoreClearData: $ignoreClearData
    ) {
      id
      success
      nextQuestion
    }
  }
`;

const updateNpoAnswerBulkQuery = gql`
  mutation bulkSaveAnswers($reportId: Int, $answers: [AnswerInput]!) {
    bulkSaveAnswers(reportId: $reportId, answers: $answers) {
      success
    }
  }
`;

const questionQuery = gql`
  query allNpoReportQuestions($reportId: Int!, $section: String) {
    allNpoReportQuestions(filters: { report: $reportId, section: $section }) {
      edges {
        node {
          dbId
          text
          questionType
          answers(filters: { reportId: $reportId }) {
            edges {
              node {
                dbId
                date1
                date2
                questionText
                text
              }
            }
          }
          enumResponses {
            edges {
              node {
                dbId
                nextQuestionId
                response
              }
            }
          }
        }
      }
    }
  }
`;

export interface FeedbackQuestion {
  dbId: number;
  questionType:
    | 'INTRO'
    | 'LONG_TEXT'
    | 'ADDRESS'
    | 'CUSTOM_OPTIONS'
    | 'SERVICE_COLLECTION'
    | 'SERVICE'
    | 'DATE'
    | 'MIXED'
    | 'TREE'
    | 'BENEFICIARIES'
    | 'RADIO'
    | 'CHECKBOXES';
  answers:
    | [
        {
          dbId: number | null;
          date1: string | null;
          date2: string | null;
          questionText: string;
          text: string;
        },
      ]
    | [];
  enumResponses: {
    dbId: number;
    nextQuestionId: number | null;
    response: string;
  }[];
  text: string;
}

export interface FeedbackQuestionsById {
  [questionId: number]: FeedbackQuestion | undefined;
}

export const mapFeedbackQuestions = (
  questions: FeedbackQuestion[],
): FeedbackQuestionsById => {
  const result: FeedbackQuestionsById = {};
  for (const question of questions) {
    result[question.dbId] = question;
  }
  return result;
};

export const getQuestion = (
  client: ApolloClient<object>,
  reportId: number,
  section: FeedbackSectionId,
) => {
  return client.query({
    query: questionQuery,
    variables: {
      reportId,
      section,
    },
    fetchPolicy: 'network-only',
  });
};

interface FeedbackNextQuestionAnswer {
  id: number;
  nextQuestion:
    | { dbId: null }
    | (FeedbackQuestion & {
        answers?: FeedbackQuestion['answers'];
      });
  success: boolean;
}

export interface FeedbackUpdateAnswerResponse {
  newNpoAnswer?: FeedbackNextQuestionAnswer;
  updateNpoAnswer?: FeedbackNextQuestionAnswer;
}

export interface UpdateAnswer {
  reportId: number | string;
  questionId: number;
  id?: number;
  ignoreClearData?: boolean;
  answerId: number | undefined;
  answer: string;
  startDate?: string;
  endDate?: string;
}

export const updateAnswer = (
  client: ApolloClient<object>,
  {
    reportId,
    questionId,
    answerId,
    id,
    ignoreClearData,
    answer,
    startDate,
    endDate,
  }: UpdateAnswer,
) => {
  const i = answerId || id;
  return client.mutate({
    mutation: i ? updateNpoAnswerQuery : newNpoAnswerQuery,
    variables: {
      reportId,
      questionId,
      text: answer,
      ignoreClearData,
      id: i,
      date1: startDate,
      date2: endDate,
    },
    fetchPolicy: 'no-cache',
  });
};

const clearImpactReportAnswersQuery = gql`
  mutation ClearImpactReportAnswers($reportId: Int!, $softDelete: Boolean!) {
    clearAnswersBySections(
      reportId: $reportId
      sectionIds: ["impact_summary", "indicator_details"]
      softDelete: $softDelete
    ) {
      success
      target
      deletedCount
      clearedSections
      softDelete
    }
  }
`;

export interface ClearImpactReportAnswersResponse {
  clearAnswersBySections: {
    success: boolean;
    target: string;
    deletedCount: number;
    clearedSections: string[];
    softDelete: boolean;
  };
}

export const clearImpactReportAnswers = (
  client: ApolloClient<object>,
  reportId: number | string,
  softDelete = false,
) => {
  return client.mutate<ClearImpactReportAnswersResponse>({
    mutation: clearImpactReportAnswersQuery,
    variables: {
      reportId: typeof reportId === 'string' ? Number(reportId) : reportId,
      softDelete,
    },
    fetchPolicy: 'no-cache',
  });
};

export const updateBulkAnswer = (
  client: ApolloClient<object>,
  reportId: number | string,
  answers: (Omit<UpdateAnswer, 'reportId' | 'answer'> & { text: string })[],
) => {
  return client.mutate({
    mutation: updateNpoAnswerBulkQuery,
    variables: {
      reportId,
      answers,
    },
    fetchPolicy: 'no-cache',
  });
};
